import React, { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { Section } from "@/components/Section";
import { Text } from "@/components/Text";
import { ConsultantProfileContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { clearNewSuggestedDataFlag, IConsultantProfile } from "@/data/consultant-profiles";
import { Flex } from "@/eworkitComponents";

import { SuggestedDataDialogModalButton } from "./SuggestedDataDialogModalButton";

import "./_new-suggested-data-section.scss";

interface INewSuggestedDataSectionProps {
  profile: IConsultantProfile;
  onProfileUpdate: (profile: IConsultantProfile, skipMessage?: boolean) => void;
}

export const NewSuggestedDataSection: FC<INewSuggestedDataSectionProps> = ({ profile, onProfileUpdate }) => {
  const ConsultantProfile = useContext(ConsultantProfileContext);

  const { t } = useTranslation("session");

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const clearSuggestedData = useCallback(async () => {
    try {
      await clearNewSuggestedDataFlag(
        { consultantProfileId: ConsultantProfile.currentProfileId, companyId: ConsultantProfile.currentCompanyId },
        {},
      );

      onProfileUpdate({ ...profile, newSuggestedData: false }, true);
    } catch (error) {
      // TODO: handle error if specified
    }
  }, [profile, onProfileUpdate]);

  const baseClass = "new-suggested-data-section";

  return (
    <Section
      className={baseClass}
      flex
      background="info"
      borderRadius
      padding={["md"]}
      direction={isSmallScreen ? "column" : "row"}
      innerMargin="lg"
      align="center">
      <Icon icon="binoculars" color="background-card" size="xl" />

      <Flex direction="column" grow={1}>
        <Header size="xs" color="background-card" weight="bold">
          {t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.SECTION.HEADER")}
        </Header>
        <Text size="md" color="background-card">
          {t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.SECTION.TEXT")}
        </Text>
      </Flex>

      <Flex direction="row" innerMargin="lg" align="center">
        <Button className={`${baseClass}__button-dismiss`} variant="primary" size="md" onClick={clearSuggestedData}>
          {t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.SECTION.BUTTON_DISMISS")}
        </Button>

        <SuggestedDataDialogModalButton
          className={`${baseClass}__button-review`}
          variant="primary"
          size="md"
          dialogProps={{ profile, onProfileUpdate }}>
          {t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.SECTION.BUTTON_REVIEW")}
        </SuggestedDataDialogModalButton>
      </Flex>
    </Section>
  );
};
